import FigurePageDetail from "../../components/Figure/FigurePageDetail";
import api from "../../lib/api";
import figureService from "../../lib/figure";
import color from "../../lib/color";
import { useRouter } from "next/router";

// Extract portrait alt from memorials data by matching the figure slug
function getPortraitAltFromMemorials(memorials, figureSlug) {
  if (!memorials || memorials.length === 0) return null;

  for (const location of memorials) {
    if (!location.memorials) continue;
    for (const memorial of location.memorials) {
      if (!memorial.persons) continue;
      for (const person of memorial.persons) {
        if (person.slug === figureSlug && person.images) {
          // Handle nested array structure: images: [[{alt: "..."}], null]
          const flatImages = person.images
            .flat()
            .filter((img) => img !== null && typeof img === "object");

          if (flatImages.length === 0) continue;

          const markedAsPortrait = flatImages.find((image) => image.portrait);
          const portrait = markedAsPortrait || flatImages[0];

          if (portrait && portrait.alt) {
            return portrait.alt;
          }
        }
      }
    }
  }
  return null;
}

export default function Figure({
  stops,
  figure,
  centers,
  memorials,
  labels,
  figureSlug,
}) {
  const { locale } = useRouter();
  const displayName = figure.title;
  const portraitUrl = figureService.getPortraitUrl(figure);
  const portraitAlt = getPortraitAltFromMemorials(memorials, figureSlug);

  stops.forEach((stop, index) => {
    if (!stop.location) {
      stop.location = {
        latitude: "0",
        longitude: "0",
        type: {
          value: null,
        },
      };
    }

    const locationColor = color.getLocationColor(
      stop.location.latitude,
      stop.location.longitude,
    );

    stop.color = color.hslToHexString(locationColor.h, locationColor.s, locationColor.l);
    stop.index = index;
    stop.duration = figureService.getDuration(stop, locale);
  });

  figureService.calculatePlaceOfHomePeriods(stops);

  const memorialsProcessed = memorials.map((memorial) => ({
    latitude: memorial.latitude || "0",
    longitude: memorial.longitude || "0",
    icon: "memorial",
    href: `/locations/memorials/${memorial.slug}#${memorial.memorials[0].slug}`,
  }));

  const centersProcessed = centers.map((center) => ({
    ...center,
    href: `/locations/${center.slug}`,
  }));

  // console.log("-------------------------- Figure", figure);

  return (
    <FigurePageDetail
      name={displayName}
      stops={stops}
      portrait={portraitUrl}
      portraitAlt={portraitAlt}
      centers={centersProcessed}
      memorials={memorialsProcessed}
      labels={labels}
      locale={locale}
    />
  );
}

export async function getStaticPaths() {
  const figures = await api.getContent("/api/collections/map-persons/entries");
  const pathsDe = figures.map((figure) => ({
    params: { slug: figure.slug },
    locale: "de",
  }));
  const pathsEn = figures.map((figure) => ({
    params: { slug: figure.slug },
    locale: "en",
  }));

  return { paths: [...pathsDe, ...pathsEn], fallback: false };
}

export async function getStaticProps({ params, locale }) {
  const slug = params.slug;
  const stopsPromise = api.getStops(slug, locale);

  const figurePromise = api.getContent(`/api/collections/map-persons/entries`, {
    fields: ["images"],
    filter: `[slug]=${slug}`,
  });
  const memorialsPromise = api.getContent(`/api/memorials/person/${slug}`, { locale });
  const centersPromise = api.getCenters(locale);

  const [stops, figure, centers, memorials] = await Promise.all([
    stopsPromise,
    figurePromise,
    centersPromise,
    memorialsPromise,
  ]);

  return await api.injectDefaultData(
    {
      props: {
        stops: stops,
        figure: figure[0],
        centers,
        memorials,
        figureSlug: slug,
      },
      revalidate: 3,
    },
    locale,
  );
}
